import { type WithChildren, type WithId } from "@common/declarations";
import { removeItem, sort } from "@common/lib/array";
import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  type PointerActivationConstraint,
  PointerSensor,
  type UniqueIdentifier,
  useSensor,
  useSensors
} from "@dnd-kit/core";
import {
  restrictToFirstScrollableAncestor,
  restrictToHorizontalAxis,
  restrictToParentElement,
  restrictToVerticalAxis
} from "@dnd-kit/modifiers";
import {
  horizontalListSortingStrategy,
  rectSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { identity } from "lodash";
import React, { type HTMLProps } from "react";

export type Axis = "x" | "y" | "xy";

export interface SortEnd {
  oldIndex: number;
  newIndex: number;
}

export type SortStartHandler = () => void;

export type SortEndHandler = (sort: SortEnd) => void;

export interface SortableContainerProps {
  axis?: Axis;
  distance?: number;
  lockTo?: "parent" | "scrollableParent";
  onSortStart?: SortStartHandler;
  onSortEnd?: SortEndHandler;
  useHandles?: boolean;
}

export type WrappedComponentFactory<P> = (props: P) => React.JSX.Element;

export type WrappedComponent<P> = React.ComponentType<P> | WrappedComponentFactory<P>;

type SortableHOCContextProps = {
  register: (id: string, index: number) => void;
  unregister: (id: string) => void;
  useHandles: boolean;
};

const SortableHOCContext = React.createContext<SortableHOCContextProps>({
  register: identity,
  unregister: identity,
  useHandles: false
});

export const SortableContainerHOC =
  <P extends {}>(WrappedComponent: WrappedComponent<P>): React.FunctionComponent<P & SortableContainerProps> =>
  (props: P & SortableContainerProps & { useHandles?: boolean }) => {
    const activationConstraint = React.useMemo(
      (): PointerActivationConstraint | undefined => ({
        distance: props.distance ?? 3
      }),
      [props.distance]
    );

    const sensors = useSensors(
      useSensor(PointerSensor, {
        activationConstraint
      }),
      useSensor(KeyboardSensor, {
        coordinateGetter: sortableKeyboardCoordinates
      })
    );

    const [items, setItems] = React.useState<Array<{ id: UniqueIdentifier; index: number }>>([]);
    const context = React.useMemo<SortableHOCContextProps>(
      () => ({
        register: (id: string, index: number) => setItems((x) => [...x, { id, index }]),
        unregister: (id: string) => {
          setItems((x) => removeItem(x, (x) => x.id === id));
        },
        useHandles: props.useHandles ?? false
      }),
      [props.useHandles]
    );

    const handleDragEnd = React.useCallback(
      (event: DragEndEvent) => {
        const { active, over } = event;

        const oldIndex = items.find((x) => x.id === active.id)!.index;
        const newIndex = over
          ? items.find((x) => x.id === over.id)!.index
          : items.find((x) => x.id === active.id)!.index;
        props.onSortEnd!({
          oldIndex,
          newIndex
        });
      },
      [props.onSortEnd, items]
    );

    const sortedItems = React.useMemo(() => sort(items, { key: "index" }), [items]);

    const axisModifiers = React.useMemo(() => {
      switch (props.axis) {
        case "x":
          return [restrictToHorizontalAxis];
        case "y":
          return [restrictToVerticalAxis];
      }
    }, [props.axis]);

    const containerModifiers = React.useMemo(() => {
      switch (props.lockTo) {
        case "parent":
          return [restrictToParentElement];
        case "scrollableParent":
          return [restrictToFirstScrollableAncestor];
      }
    }, [props.lockTo]);

    const modifiers = React.useMemo(
      () =>
        axisModifiers || containerModifiers ? [...(axisModifiers ?? []), ...(containerModifiers ?? [])] : undefined,
      [axisModifiers, containerModifiers]
    );

    const strategy = React.useMemo(() => {
      switch (props.axis) {
        case "x":
          return horizontalListSortingStrategy;
        case "y":
          return verticalListSortingStrategy;
        case "xy":
          return rectSortingStrategy;
      }
    }, [props.axis]);

    return (
      <DndContext
        collisionDetection={closestCenter}
        modifiers={modifiers}
        onDragEnd={props.onSortEnd ? handleDragEnd : undefined}
        onDragStart={props.onSortStart}
        sensors={sensors}
      >
        <SortableHOCContext.Provider value={context}>
          <SortableContext items={sortedItems} strategy={strategy}>
            <WrappedComponent {...props} />
          </SortableContext>
        </SortableHOCContext.Provider>
      </DndContext>
    );
  };

export type SortableElementRequiredProps = WithId<string> & {
  index: number;
};

export type SortableElementHOCAddedProps = {
  setRef: (node: HTMLElement | null) => void;
  handleProps: SortableElementHandleProps;
  isDragging: boolean;
  style: React.CSSProperties;
};
export const SortableElementHOC =
  <P extends {}>(
    WrappedComponent: WrappedComponent<P>
  ): React.FunctionComponent<Omit<P & SortableElementRequiredProps, "setRef">> =>
  (props: P & SortableElementRequiredProps) => {
    const { attributes, isDragging, listeners, setNodeRef, transform, transition } = useSortable({
      animateLayoutChanges: () => false,
      id: props.id
    });

    const style = {
      transform: CSS.Translate.toString(transform),
      transition
    };

    const context = React.useContext(SortableHOCContext);

    React.useEffect(() => {
      context.register(props.id, props.index);

      return () => {
        context.unregister(props.id);
      };
    }, [props.id, props.index]);

    const wcp: P = {
      ...props,
      style
    };
    if (!context.useHandles) {
      Object.assign(wcp, {
        ...attributes,
        ...listeners
      });
    }
    return (
      <WrappedComponent
        {...wcp}
        setRef={setNodeRef}
        handleProps={{
          ...attributes,
          ...listeners
        }}
        isDragging={isDragging}
      />
    );
  };

export type SortableElementHandleProps = Omit<HTMLProps<any>, "ref">;
export type SortableElementChildrenProps = {
  handleProps: SortableElementHandleProps;
};
type SortableElementProps = SortableElementRequiredProps & {
  children: (props: SortableElementChildrenProps) => any;
  className?: string;
  style?: React.CSSProperties;
  tag?: string;
};
export const SortableElement = (props: SortableElementProps) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
    animateLayoutChanges: () => false,
    id: props.id
  });

  const style = {
    transform: CSS.Translate.toString(transform),
    transition
  };

  const context = React.useContext(SortableHOCContext);

  React.useEffect(() => {
    context.register(props.id, props.index);

    return () => {
      context.unregister(props.id);
    };
  }, [props.id, props.index]);

  return (
    <div ref={setNodeRef} className={props.className} style={{ ...style, ...props.style }}>
      {props.children({ handleProps: { ...attributes, ...listeners } })}
    </div>
  );
};

export const SortableContainer = SortableContainerHOC((props: WithChildren & SortableContainerProps) => (
  <div>{props.children}</div>
));
